"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { NewsAPI } from "@/api/news/news.api";
import PaginationCustom from "@/common/PaginationCustom";
import { NewsCard } from "@/components/ui/NewsCard";
import { NewsInterface } from "@/types/news";
import { Newspaper, Megaphone, Globe, ArrowRight, Clock, User } from "lucide-react";
import { FormatUtils } from "@/utils/format";
import Link from "next/link";

const NewsSection = () => {
  const [news, setNews] = useState<NewsInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const isMountedRef = useRef(false);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const limit = 8;
      const res = await NewsAPI.getNews({ currentPage, limit });
      

      if (res.status === 200) {
        const { articles, total } = res.data as {
          articles: NewsInterface[];
          total: number;
        };
        setNews(articles);
        setTotalPages(Math.ceil(total / limit));
      }
    } catch (err) {
      console.error("Failed to fetch news:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    if (isMountedRef.current) {
      fetchNews();
    } else {
      isMountedRef.current = true;
      fetchNews();
    }
  }, [fetchNews,]);



  const NewsCardCustom = ({news}:{news:NewsInterface}) => {
  
  return (
    <Link href={`/tin-tuc/${news.slug}`} className="group bg-white  rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-red-200">
      <div className="flex flex-col md:flex-row">
        {/* Image Section */}
        <div className="md:w-80 h-56 md:h-auto relative overflow-hidden flex-shrink-0">
          <img
            src={news.imageUrl}
            alt={news.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Category Badge */}
          {/* <div className="absolute top-4 left-4">
            <span className="bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-lg">
              {news.category}
            </span>
          </div> */}
        </div>

        {/* Content Section */}
        <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors duration-300">
              {news.title}
            </h2>
            
            <p className="text-gray-600 text-base leading-relaxed mb-4 line-clamp-2">
              {news.shortDescription}
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              {/* <div className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                <span>{news.author}</span>
              </div> */}
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>    {FormatUtils.formatDate(news.createdAt)}</span>
              </div>
            </div>

            <button className="flex items-center gap-2 text-red-600 font-medium hover:gap-3 transition-all duration-300 group/btn">
              <span className="text-sm">Đọc thêm</span>
              <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
};

  const NewsSkeletonLoader = () => (
    <div className="space-y-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row">
            {/* Image skeleton */}
            <div className="md:w-80 h-56 md:h-64 flex-shrink-0">
              <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
            </div>
            {/* Content skeleton */}
            <div className="flex-1 p-6 md:p-8 space-y-4">
              <div className="h-6 w-3/4 rounded-lg bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
              <div className="h-4 w-full rounded-lg bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
              <div className="h-4 w-5/6 rounded-lg bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
              <div className="flex items-center gap-4 pt-4">
                <div className="h-3 w-24 rounded-lg bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
                <div className="h-3 w-32 rounded-lg bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_2s_infinite]" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-flex items-center justify-center space-x-2 bg-red-50 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Megaphone className="w-4 h-4" />
            <span>Tin tức mới nhất</span>
          </div>

          <h1 className="text-4xl md:text-7xl font-bold text-gray-900 mb-6">
            Cập nhật{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-pink-500">
              Tin tức
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Nắm bắt xu hướng, thông tin và hoạt động mới nhất của chúng tôi
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="bg-red-100 p-2 rounded-full">
                <Newspaper className="w-5 h-5 text-red-600" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-bold text-gray-900">120+</div>
                <div className="text-sm">Bài viết đã đăng</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="bg-pink-100 p-2 rounded-full">
                <Globe className="w-5 h-5 text-pink-600" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-bold text-gray-900">10+</div>
                <div className="text-sm">Chuyên mục nội dung</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <NewsSkeletonLoader />
        ) : (
          <>
            {/* News List - Changed from grid to vertical stack */}
            <div className="space-y-6 mb-12">
              {news.map((item) => (
                <NewsCardCustom key={item.id} news={item} />
              ))}
            </div>

            {/* Empty State */}
            {news.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="bg-gray-100 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                  <Newspaper className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Không tìm thấy bài viết
                </h3>
                <p className="text-gray-600">Vui lòng thử lại sau.</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationCustom
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Background Decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-100 to-pink-100 rounded-full opacity-50 animate-pulse" />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-100 to-yellow-100 rounded-full opacity-50 animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};

export default NewsSection;